// utils/ukSearchSystem.js - UK-specific search system
import { unitedKingdomFeedsConfig } from './unitedKingdomFeedsConfig.js';
import { unitedKingdomDepartmentConfig } from './unitedKingdomDepartmentConfig.js';

// UK region information lookup table
const ukRegionInfo = {
  england: { fullName: 'England', code: 'eng', flagImage: '/englandFlag.png', tableName: 'england' },
  scotland: { fullName: 'Scotland', code: 'sct', flagImage: '/scotlandFlag.png', tableName: 'scotland' },
  wales: { fullName: 'Wales', code: 'wal', flagImage: '/walesFlag.png', tableName: 'wales' },
  northernireland: { fullName: 'Northern Ireland', code: 'ni', flagImage: '/northernIrelandFlag.png', tableName: 'northern_ireland' }
};

// Search relevant pages for UK - similar to US/Canada/Mexico but UK-specific
export const searchUKRelevantPages = (query, limit = 12) => {
  if (!query || !query.trim()) return [];
  
  const normalizedQuery = query.toLowerCase().trim();
  const results = [];
  
  // console.log('=== UK SEARCH DEBUG ===');
  // console.log('Query:', normalizedQuery);

  // 1. Search UK regions - exact name matching
  Object.entries(ukRegionInfo).forEach(([regionKey, regionData]) => {
    const fullName = regionData.fullName.toLowerCase();
    
    if (fullName.includes(normalizedQuery) || normalizedQuery.includes(fullName)) {
      // Main region page
      results.push({
        title: `${regionData.fullName} Page`,
        description: `${regionData.fullName} government information and updates`,
        route: `/uk/${regionKey}`,
        icon: 'Map',
        flagImage: regionData.flagImage, // Add flag image support
        type: 'region',
        priority: 1
      });
      
      // Region feeds
      results.push({
        title: `${regionData.fullName} Updates`,
        description: `View all updates from ${regionData.fullName}`,
        route: `/feed/united_kingdom/${regionData.tableName}`,
        icon: 'FileText',
        type: 'feed',
        priority: 2
      });
      
      // console.log('Found UK region match:', regionData.fullName);
    }
  });

  // 2. Search departments/ministries
  Object.entries(unitedKingdomDepartmentConfig).forEach(([deptKey, deptData]) => {
    if (!deptData.departmentInfo) return;
    
    const deptName = deptData.departmentInfo.name.toLowerCase();
    
    // Handle various UK department name patterns
    if (deptName.includes(normalizedQuery) || 
        deptName.replace('department for ', '').includes(normalizedQuery) ||
        deptName.replace('department of ', '').includes(normalizedQuery) ||
        deptName.replace('ministry of ', '').includes(normalizedQuery) ||
        deptName.replace('office of ', '').includes(normalizedQuery) ||
        deptName.replace('hm ', '').includes(normalizedQuery)) {
      
      // Main department page
      results.push({
        title: deptData.departmentInfo.name,
        description: deptData.departmentInfo.missionStatement || 'UK government department information and services',
        route: deptData.departmentInfo.route,
        icon: 'Building',
        type: 'department',
        priority: 1
      });

      // Add department main feeds
      if (deptData.mainFeeds && deptData.mainFeeds.length > 0) {
        deptData.mainFeeds.forEach(feed => {
          if (feed.apiParams) {
            const schemaMatch = feed.apiParams.match(/schema=([^&]+)/);
            const tableMatch = feed.apiParams.match(/table=([^&]+)/);
            
            if (schemaMatch && tableMatch) {
              const schema = decodeURIComponent(schemaMatch[1]);
              const table = decodeURIComponent(tableMatch[1]);
              
              let feedRoute = `/feed/${schema}/${table}`;
              
              const filterMatch = feed.apiParams.match(/filter_column=([^&]+)&filter_string=([^&]+)/);
              if (filterMatch) {
                const filterColumn = decodeURIComponent(filterMatch[1]);
                const filterString = decodeURIComponent(filterMatch[2]);
                feedRoute += `?filter_column=${filterColumn}&filter_string=${encodeURIComponent(filterString)}`;
              }
              
              results.push({
                title: `${deptData.departmentInfo.name} - ${feed.name}`,
                description: `View all ${feed.name.toLowerCase()} from ${deptData.departmentInfo.name}`,
                route: feedRoute,
                icon: 'List',
                type: 'feed',
                priority: 2
              });
            }
          }
        });
      }

      // console.log('Found UK department match:', deptData.departmentInfo.name);
    }
  });

  // 3. Search sections (legislative, executive, judicial, etc.)
  const sectionMappings = {
    'legislative': { 
      title: 'Legislative Branch', 
      description: 'Parliament, bills, House of Commons, House of Lords'
    },
    'executive': { 
      title: 'Executive Branch', 
      description: 'Government ministries and departments'
    },
    'judicial': { 
      title: 'Judicial Branch', 
      description: 'Supreme Court decisions and judicial proceedings'
    },
    'economy': { 
      title: 'Economy Section', 
      description: 'Treasury, business, trade, and economic policy'
    },
    'environment': { 
      title: 'Environment Section', 
      description: 'Environmental policy, energy, and rural affairs'
    },
    'defense': { 
      title: 'Defense Section', 
      description: 'Ministry of Defence and national security'
    },
    'foreign-affairs': { 
      title: 'Foreign Affairs', 
      description: 'International relations and development'
    },
    'health': { 
      title: 'Health Section', 
      description: 'Public health and social care'
    },
    'infrastructure': { 
      title: 'Infrastructure Section', 
      description: 'Transport, housing, and communities'
    },
    'justice': { 
      title: 'Justice Section', 
      description: 'Ministry of Justice and Attorney General'
    },
    'science': { 
      title: 'Science & Technology', 
      description: 'Innovation, research, and technology policy'
    }
  };

  Object.entries(sectionMappings).forEach(([sectionKey, sectionInfo]) => {
    const sectionTitle = sectionInfo.title.toLowerCase();
    
    if (sectionKey.includes(normalizedQuery) || 
        sectionTitle.includes(normalizedQuery) ||
        normalizedQuery === sectionKey) {
      
      // Main section page
      results.push({
        title: sectionInfo.title,
        description: sectionInfo.description,
        route: `/uk/${sectionKey}`,
        icon: 'Globe',
        type: 'section',
        priority: 3
      });

      // Add feeds from this section
      if (unitedKingdomFeedsConfig[sectionKey] && Array.isArray(unitedKingdomFeedsConfig[sectionKey])) {
        unitedKingdomFeedsConfig[sectionKey].forEach(feed => {
          if (feed.apiEndpoint && feed.title && feed.showOnHomePage !== false) {
            const schemaMatch = feed.apiEndpoint.match(/schema=([^&]+)/);
            const tableMatch = feed.apiEndpoint.match(/table=([^&]+)/);
            
            if (schemaMatch && tableMatch) {
              const schema = decodeURIComponent(schemaMatch[1]);
              const table = decodeURIComponent(tableMatch[1]);
              
              let feedRoute = `/feed/${schema}/${table}`;
              
              const filterMatch = feed.apiEndpoint.match(/filter_column=([^&]+)&filter_string=([^&]+)/);
              if (filterMatch) {
                const filterColumn = decodeURIComponent(filterMatch[1]);
                const filterString = decodeURIComponent(filterMatch[2]);
                feedRoute += `?filter_column=${filterColumn}&filter_string=${encodeURIComponent(filterString)}`;
              }
              
              results.push({
                title: feed.title,
                description: `View all items in the ${feed.title.toLowerCase()} feed`,
                route: feedRoute,
                icon: 'List',
                type: 'feed',
                priority: 4
              });
            }
          }
        });
      }

      // console.log('Found UK section match:', sectionInfo.title);
    }
  });

  // 4. Search individual feeds by name
  Object.entries(unitedKingdomFeedsConfig).forEach(([sectionKey, sectionFeeds]) => {
    if (!Array.isArray(sectionFeeds)) return;
    
    sectionFeeds.forEach(feed => {
      if (!feed.title || !feed.apiEndpoint) return;
      
      const feedTitle = feed.title.toLowerCase();
      const feedName = (feed.feedName || '').toLowerCase();
      
      if (feedTitle.includes(normalizedQuery) || 
          feedName.includes(normalizedQuery) ||
          normalizedQuery.includes(feedTitle)) {
        
        const schemaMatch = feed.apiEndpoint.match(/schema=([^&]+)/);
        const tableMatch = feed.apiEndpoint.match(/table=([^&]+)/);
        
        if (schemaMatch && tableMatch) {
          const schema = decodeURIComponent(schemaMatch[1]);
          const table = decodeURIComponent(tableMatch[1]);
          
          let feedRoute = `/feed/${schema}/${table}`;
          
          const filterMatch = feed.apiEndpoint.match(/filter_column=([^&]+)&filter_string=([^&]+)/);
          if (filterMatch) {
            const filterColumn = decodeURIComponent(filterMatch[1]);
            const filterString = decodeURIComponent(filterMatch[2]);
            feedRoute += `?filter_column=${filterColumn}&filter_string=${encodeURIComponent(filterString)}`;
          }
          
          results.push({
            title: feed.title,
            description: `View all items in the ${feed.title.toLowerCase()} feed`,
            route: feedRoute,
            icon: 'List',
            type: 'feed',
            priority: 3
          });
          
          // console.log('Found UK feed match:', feed.title);
        }
      }
    });
  });

  // Remove duplicates and sort by priority
  const uniqueResults = results.filter((item, index, self) => 
    index === self.findIndex(t => t.route === item.route)
  );

  const finalResults = uniqueResults
    .sort((a, b) => a.priority - b.priority)
    .slice(0, limit);

  // console.log('Final UK results:', finalResults.length);
  // console.log('=== END UK SEARCH ===');
  
  return finalResults;
};

// Enhanced content type detection for UK
export const enrichUKSearchResult = (item) => {
  try {
    const title = (item.title || '').toLowerCase();
    const description = (item.description || item.snippet || '').toLowerCase();
    const source = (item.source || '').toLowerCase();
    const collectionName = (item.collection_name || '').toLowerCase();

    // Check for UK department patterns
    for (const [deptKey, deptConfig] of Object.entries(unitedKingdomDepartmentConfig)) {
      if (!deptConfig.departmentInfo) continue;
      
      const deptName = deptConfig.departmentInfo.name.toLowerCase();
      const deptKeywords = [
        deptName,
        deptConfig.departmentInfo.name.replace(/department for /i, ''),
        deptConfig.departmentInfo.name.replace(/department of /i, ''),
        deptConfig.departmentInfo.name.replace(/ministry of /i, ''),
        deptConfig.departmentInfo.name.replace(/office of /i, ''),
        deptConfig.departmentInfo.name.replace(/hm /i, ''),
        deptKey
      ];

      if (deptKeywords.some(keyword => 
        title.includes(keyword) || 
        source.includes(keyword) || 
        collectionName.includes(keyword)
      )) {
        return {
          type: 'department',
          departmentKey: deptKey,
          department: deptConfig.departmentInfo,
          route: deptConfig.departmentInfo.route
        };
      }
    }

    // Check for UK region patterns
    for (const [regionKey, regionData] of Object.entries(ukRegionInfo)) {
      const regionName = regionData.fullName.toLowerCase();
      
      if (title.includes(regionName) || 
          source.includes(regionName) || 
          collectionName.includes(regionName)) {
        return {
          type: 'region',
          regionKey: regionKey,
          region: regionData,
          route: `/uk/${regionKey}`
        };
      }
    }

    // Check for section patterns
    const sectionKeywords = {
      'legislative': ['parliament', 'house of commons', 'house of lords', 'bill', 'legislation'],
      'executive': ['government', 'ministry', 'department', 'minister', 'secretary'],
      'judicial': ['supreme court', 'court', 'tribunal', 'justice', 'judicial'],
      'defense': ['defence', 'defense', 'military', 'armed forces', 'mod'],
      'economy': ['treasury', 'economic', 'business', 'trade', 'finance'],
      'environment': ['environment', 'energy', 'climate', 'defra', 'net zero'],
      'health': ['health', 'nhs', 'social care', 'medical']
    };

    for (const [sectionKey, keywords] of Object.entries(sectionKeywords)) {
      if (keywords.some(keyword => title.includes(keyword) || description.includes(keyword))) {
        return {
          type: 'section',
          sectionKey: sectionKey,
          route: `/uk/${sectionKey}`
        };
      }
    }

    return { type: 'document' };
  } catch (error) {
    console.warn('Error in enrichUKSearchResult:', error);
    return { type: 'document' };
  }
};

// Export functions for use in components
export const getUKNavigationSuggestions = (query, limit = 6) => {
  if (!query || !query.trim()) return [];
  
  const suggestions = searchUKRelevantPages(query, limit);
  
  return suggestions.map(suggestion => ({
    title: suggestion.title,
    description: suggestion.description,
    route: suggestion.route,
    type: suggestion.type,
    icon: getIconForType(suggestion.type),
    flagImage: suggestion.flagImage, // Include flag image for regions
    priority: suggestion.priority || 5
  }));
};

const getIconForType = (type) => {
  const iconMap = {
    'department': 'Building',
    'ministry': 'Building',
    'region': 'Map',
    'section': 'Globe',
    'feed': 'List',
    'document': 'FileText'
  };
  
  return iconMap[type] || 'InfoCircle';
};

export default {
  searchUKRelevantPages,
  enrichUKSearchResult,
  getUKNavigationSuggestions
};